import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus } from 'lucide-react';
import { InsuranceMetricsCards } from '@/components/InsuranceMetricsCards';
import { InsurancePolicyList } from '@/components/InsurancePolicyList';
import { InsurancePolicyForm } from '@/components/InsurancePolicyForm';
import { UpcomingPaymentsList } from '@/components/UpcomingPaymentsList';
import { useInsurancePolicies } from '@/hooks/useInsurancePolicies';
import { useCreateInsurancePolicy } from '@/hooks/useCreateInsurancePolicy';
import { useUpdateInsurancePolicy } from '@/hooks/useUpdateInsurancePolicy';
import { useDeleteInsurancePolicy } from '@/hooks/useDeleteInsurancePolicy';
import { InsurancePolicyFormData } from '@lib/validators/insurance';
import { PolicyWithStatus } from '@/types/insurance';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

export default function Insurance() {
  const { data: policies, isLoading, error } = useInsurancePolicies();
  const createPolicy = useCreateInsurancePolicy();
  const updatePolicy = useUpdateInsurancePolicy();
  const deletePolicy = useDeleteInsurancePolicy();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<PolicyWithStatus | null>(null);

  const handleCreatePolicy = async (data: InsurancePolicyFormData) => {
    try {
      // Ensure all required fields are present
      const policyData = {
        policy_type: data.policy_type,
        provider_name: data.provider_name,
        policy_number: data.policy_number,
        coverage_amount: data.coverage_amount,
        premium_amount: data.premium_amount,
        premium_frequency: data.premium_frequency,
        currency: data.currency,
        start_date: data.start_date,
        end_date: data.end_date,
        deductible_amount: data.deductible_amount,
        beneficiary_name: data.beneficiary_name,
        beneficiary_relationship: data.beneficiary_relationship,
        provider_contact: data.provider_contact,
        notes: data.notes,
      };
      await createPolicy.mutateAsync(policyData);
      toast.success('Insurance policy created successfully');
      setIsCreateDialogOpen(false);
    } catch (error: any) {
      toast.error(error.message || 'Failed to create policy');
      console.error(error);
    }
  };

  const handleEditPolicy = (policy: PolicyWithStatus) => {
    setSelectedPolicy(policy);
    setIsEditDialogOpen(true);
  };

  const handleUpdatePolicy = async (data: InsurancePolicyFormData) => {
    if (!selectedPolicy) return;

    try {
      await updatePolicy.mutateAsync({
        id: selectedPolicy.id,
        ...data,
      });
      toast.success('Insurance policy updated successfully');
      setIsEditDialogOpen(false);
      setSelectedPolicy(null);
    } catch (error: any) {
      toast.error(error.message || 'Failed to update policy');
      console.error(error);
    }
  };

  const handleDeleteClick = (policyId: string) => {
    const policy = policies?.find(p => p.id === policyId);
    if (policy) {
      setSelectedPolicy(policy);
      setIsDeleteDialogOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedPolicy) return;

    try {
      await deletePolicy.mutateAsync(selectedPolicy.id);
      toast.success('Insurance policy deleted successfully');
      setIsDeleteDialogOpen(false);
      setSelectedPolicy(null);
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete policy');
      console.error(error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Insurance</h1>
          <p className="text-muted-foreground">
            Manage your insurance policies and track premium payments.
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> Add Policy
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load insurance policies. Please try again later.
          </AlertDescription>
        </Alert>
      )}

      {/* Metrics Cards */}
      <InsuranceMetricsCards />

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Policy List - Takes 2 columns on large screens */}
        <div className="lg:col-span-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-muted-foreground">Loading policies...</p>
            </div>
          ) : (
            <InsurancePolicyList
              policies={policies || []}
              onEdit={handleEditPolicy}
              onDelete={handleDeleteClick}
            />
          )}
        </div>

        {/* Upcoming Payments - Takes 1 column on large screens */}
        <div className="lg:col-span-1">
          <UpcomingPaymentsList />
        </div>
      </div>

      {/* Create Policy Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="w-[95vw] sm:w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Insurance Policy</DialogTitle>
            <DialogDescription>
              Add a new insurance policy to track coverage and premiums.
            </DialogDescription>
          </DialogHeader>
          <InsurancePolicyForm
            onSubmit={handleCreatePolicy}
            isLoading={createPolicy.isPending}
            mode="create"
          />
        </DialogContent>
      </Dialog>

      {/* Edit Policy Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="w-[95vw] sm:w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Insurance Policy</DialogTitle>
            <DialogDescription>
              Update the details of your insurance policy.
            </DialogDescription>
          </DialogHeader>
          {selectedPolicy && (
            <InsurancePolicyForm
              onSubmit={handleUpdatePolicy}
              defaultValues={selectedPolicy}
              isLoading={updatePolicy.isPending}
              mode="edit"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Insurance Policy</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedPolicy?.provider_name}" {selectedPolicy?.policy_type} policy?
              This action cannot be undone. Premium payment transactions will be preserved but unlinked from this policy.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedPolicy(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
